import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Pencil, Trash2, FilterX } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", salesman_id: "", store_ids: [], scheduled_date: "", notes: "" };

export default function Routes() {
  const [routes, setRoutes] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterRouteId, setFilterRouteId] = useState("all");
  const [filterSalesmanId, setFilterSalesmanId] = useState("all");

  const load = async () => {
    const [r, sm, st] = await Promise.all([
      api.get("/routes"),
      api.get("/users/salesmen"),
      api.get("/stores"),
    ]);
    setRoutes(r.data);
    setSalesmen(sm.data);
    setStores(st.data);
  };
  useEffect(() => {
    load();
  }, []);

  const filteredRoutes = useMemo(() => {
    return routes.filter((r) => {
      if (filterRouteId !== "all" && r.id !== filterRouteId) return false;
      if (filterSalesmanId !== "all" && r.salesman_id !== filterSalesmanId) return false;
      if (filterDateFrom && (!r.scheduled_date || r.scheduled_date < filterDateFrom)) return false;
      if (filterDateTo && (!r.scheduled_date || r.scheduled_date > filterDateTo)) return false;
      return true;
    });
  }, [routes, filterDateFrom, filterDateTo, filterRouteId, filterSalesmanId]);

  const clearFilters = () => {
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterRouteId("all");
    setFilterSalesmanId("all");
  };

  const openNew = () => {
    setForm({ ...empty, scheduled_date: new Date().toISOString().slice(0, 10) });
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      name: r.name,
      salesman_id: r.salesman_id,
      store_ids: r.store_ids || [],
      scheduled_date: r.scheduled_date || "",
      notes: r.notes || "",
    });
    setEditingId(r.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.salesman_id) return toast.error("Select a salesman");
    if (form.store_ids.length === 0) return toast.error("Pick at least one store");
    try {
      if (editingId) {
        await api.put(`/routes/${editingId}`, form);
        toast.success("Route updated");
      } else {
        await api.post("/routes", form);
        toast.success("Route created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this route?")) return;
    await api.delete(`/routes/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleStore = (sid) => {
    setForm((f) => ({
      ...f,
      store_ids: f.store_ids.includes(sid) ? f.store_ids.filter((x) => x !== sid) : [...f.store_ids, sid],
    }));
  };

  return (
    <div className="space-y-5" data-testid="admin-routes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Field planning</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Routes</h1>
          <p className="text-[color:var(--muted)] mt-1">Assign daily beats to your salesmen.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="add-route-button">
              <Plus className="h-4 w-4 mr-1" /> New route
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="route-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit route" : "New route"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Route name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="route-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Salesman</Label>
                  <Select value={form.salesman_id} onValueChange={(v) => setForm({ ...form, salesman_id: v })}>
                    <SelectTrigger data-testid="route-salesman-trigger"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {salesmen.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Scheduled date</Label>
                  <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Stores ({form.store_ids.length})</Label>
                <div className="max-h-56 overflow-y-auto border border-[color:var(--border)] rounded-md divide-y">
                  {stores.map((s) => (
                    <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[color:var(--surface-2)]" data-testid={`route-store-${s.id}`}>
                      <Checkbox checked={form.store_ids.includes(s.id)} onCheckedChange={() => toggleStore(s.id)} />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="text-xs text-[color:var(--muted)]">{s.store_type} · {s.address}</div>
                      </div>
                    </label>
                  ))}
                  {stores.length === 0 && <div className="p-3 text-sm text-[color:var(--muted)]">Create stores first.</div>}
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="save-route-button">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white p-4 ink-shadow flex flex-wrap items-end gap-3">
        <div>
          <label className="overline text-[color:var(--muted)]">From</label>
          <Input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="h-10 w-44"
            data-testid="routes-from-input"
          />
        </div>
        <div>
          <label className="overline text-[color:var(--muted)]">To</label>
          <Input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="h-10 w-44"
            data-testid="routes-to-input"
          />
        </div>
        <div className="min-w-[200px]">
          <label className="overline text-[color:var(--muted)]">Route</label>
          <Select value={filterRouteId} onValueChange={setFilterRouteId}>
            <SelectTrigger className="h-10" data-testid="routes-name-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All routes</SelectItem>
              {routes.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <label className="overline text-[color:var(--muted)]">Salesman</label>
          <Select value={filterSalesmanId} onValueChange={setFilterSalesmanId}>
            <SelectTrigger className="h-10" data-testid="routes-salesman-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salesmen</SelectItem>
              {salesmen.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <Button
            variant="outline"
            onClick={clearFilters}
            className="h-10"
            data-testid="routes-clear-filter"
          >
            <FilterX className="h-4 w-4 mr-1" /> Clear
          </Button>
        </div>
        <div className="basis-full text-xs text-[color:var(--muted)]">
          Showing {filteredRoutes.length} of {routes.length} routes
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredRoutes.map((r) => (
          <div key={r.id} className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow" data-testid={`route-card-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="overline">{r.scheduled_date || "Unscheduled"}</p>
                <h3 className="font-display text-xl font-bold mt-1">{r.name}</h3>
                <p className="text-sm text-[color:var(--muted)] mt-0.5">{r.salesman?.name || "Unassigned"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(r)} className="p-1.5 text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-testid={`edit-route-${r.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(r.id)} className="p-1.5 text-[color:var(--muted)] hover:text-[color:var(--primary)]" data-testid={`delete-route-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 text-sm text-[color:var(--muted)]">
              {r.stores?.length || 0} stops
            </div>
            <ul className="mt-2 space-y-1 text-sm max-h-40 overflow-y-auto">
              {(r.stores || []).map((s, i) => (
                <li key={s.id} className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--muted)] flex items-center justify-center text-xs">
                    {i + 1}
                  </span>
                  <span className="truncate">{s.name}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {filteredRoutes.length === 0 && (
          <div className="col-span-full text-center py-12 text-[color:var(--muted)] rounded-xl border border-dashed border-[color:var(--border)]">
            {routes.length === 0
              ? "No routes yet. Create your first beat."
              : "No routes match these filters."}
          </div>
        )}
      </div>
    </div>
  );
}
